import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Loader2, RotateCcw, DollarSign, Check } from "lucide-react";

/**
 * AdminFareTable — per-country × per-category editable pricing grid.
 *
 * Each row edits {base_fare, per_km_rate, min_fare}. Blank a field to clear
 * that specific override and fall back to the code default. "Reset" clears
 * the whole row's override. "Reset all" wipes every admin override.
 */
export default function AdminFareTable() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [country, setCountry] = useState(null);
  const [drafts, setDrafts] = useState({}); // {cat: {base_fare, per_km_rate, min_fare}}
  const [saving, setSaving] = useState({}); // {cat: bool}

  const load = () => {
    api.get("/admin/fare-table").then((r) => {
      setData(r.data);
      setCountry((prev) => prev || (r.data.countries?.[0] || "US"));
    }).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load(); }, []);

  if (!data) {
    return (
      <div className="bg-ink-surface border border-ink-border rounded-2xl p-6 text-center text-ink-textDim" data-testid="fare-table-loading">
        <Loader2 className="animate-spin inline-block" size={16} /> {t("common.loading", "Loading…")}
      </div>
    );
  }

  const rowsForCountry = data.rows.filter((r) => r.country === country);
  const currency = rowsForCountry[0]?.currency || "usd";

  const editKey = (cat, field) => `${cat}__${field}`;
  const getDraft = (cat, field) => {
    const d = drafts[cat] || {};
    if (field in d) return d[field];
    const row = rowsForCountry.find((r) => r.category === cat) || {};
    const v = row[field];
    return v == null ? "" : String(v);
  };
  const setDraft = (cat, field, val) => setDrafts((prev) => ({ ...prev, [cat]: { ...(prev[cat] || {}), [field]: val } }));

  const saveRow = async (cat) => {
    const parse = (v) => (v === "" || v == null ? null : Number(v));
    const payload = {
      country, category: cat,
      base_fare: parse(getDraft(cat, "base_fare")),
      per_km_rate: parse(getDraft(cat, "per_km_rate")),
      per_minute_rate: parse(getDraft(cat, "per_minute_rate")),
      min_fare: parse(getDraft(cat, "min_fare")),
    };
    setSaving((s) => ({ ...s, [cat]: true }));
    try {
      await api.patch("/admin/fare-table", payload);
      toast.success(t("admin.fareTable.saved", `Saved · ${country}/${cat}`));
      setDrafts((prev) => { const n = { ...prev }; delete n[cat]; return n; });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving((s) => ({ ...s, [cat]: false })); }
  };

  const resetRow = async (cat) => {
    setSaving((s) => ({ ...s, [cat]: true }));
    try {
      await api.post("/admin/fare-table/reset", { country, category: cat });
      toast.success(t("admin.fareTable.reset", `Reset · ${country}/${cat}`));
      setDrafts((prev) => { const n = { ...prev }; delete n[cat]; return n; });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving((s) => ({ ...s, [cat]: false })); }
  };

  const resetAll = async () => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!confirm(t("admin.fareTable.resetAllConfirm", "Reset ALL admin fare overrides back to code defaults?"))) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/fare-table/reset", { all: true });
      toast.success(t("admin.fareTable.resetAllDone", `Cleared ${r.cleared} overrides`));
      setDrafts({});
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-ink-surface border border-ink-border rounded-2xl p-4" data-testid="admin-fare-table">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-brand-accent/15 flex items-center justify-center">
            <DollarSign size={16} className="text-brand-accent" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-black tracking-widest text-ink-textDim">
              {t("admin.fareTable.title", "Fare table")}
            </div>
            <div className="text-sm font-black">{t("admin.fareTable.subtitle", "Country × category pricing")}</div>
          </div>
        </div>
        <button
          onClick={resetAll}
          disabled={busy}
          className="inline-flex items-center gap-1 text-[10px] uppercase font-black text-ink-textDim px-2 py-1 rounded-full border border-ink-border hover:text-white hover:border-white/50 disabled:opacity-50"
          data-testid="fare-table-reset-all"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />} {t("admin.fareTable.resetAll", "Reset all")}
        </button>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1" data-testid="fare-table-country-tabs">
        {data.countries.map((c) => (
          <button
            key={c}
            onClick={() => setCountry(c)}
            className={`flex-shrink-0 rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-widest ${country === c ? "bg-white text-black" : "bg-ink-surface2 border border-ink-border text-ink-textDim"}`}
            data-testid={`fare-table-tab-${c}`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="text-[10px] uppercase font-black text-ink-textFaint mt-2 mb-1">
        {t("admin.fareTable.currency", "Currency")}: <span className="text-ink-text">{currency.toUpperCase()}</span>
      </div>

      <div className="mt-2 space-y-2">
        {rowsForCountry.map((row) => {
          const cat = row.category;
          const dirty = !!drafts[cat];
          return (
            <div key={cat} className="grid grid-cols-[80px,1fr,1fr,1fr,1fr,auto] gap-2 items-center bg-ink-surface2 border border-ink-border rounded-xl px-2 py-2" data-testid={`fare-row-${cat}`}>
              <div className="flex items-center gap-1">
                <span className="text-[11px] font-black uppercase">{cat}</span>
                {row.is_overridden && !dirty && <Check size={10} className="text-brand-accent" title="Override active" />}
              </div>
              {["base_fare", "per_km_rate", "per_minute_rate", "min_fare"].map((f) => (
                <label key={f} className="block">
                  <div className="text-[9px] uppercase font-black text-ink-textFaint">
                    {f === "base_fare" ? t("admin.fareTable.base", "Base")
                      : f === "per_km_rate" ? t("admin.fareTable.perKm", "Per km")
                      : f === "per_minute_rate" ? t("admin.fareTable.perMin", "Per min")
                      : t("admin.fareTable.min", "Min")}
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={getDraft(cat, f)}
                    onChange={(e) => setDraft(cat, f, e.target.value)}
                    className="w-full bg-ink-surface border border-ink-border rounded-md px-2 py-1 text-xs outline-none focus:border-white text-white"
                    data-testid={`fare-input-${cat}-${f}`}
                  />
                </label>
              ))}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => saveRow(cat)}
                  disabled={saving[cat] || !dirty}
                  className="rounded-full bg-brand-accent text-black px-2 py-1 text-[10px] font-black inline-flex items-center gap-1 disabled:opacity-40"
                  data-testid={`fare-save-${cat}`}
                >
                  {saving[cat] ? <Loader2 size={10} className="animate-spin" /> : null}
                  {t("common.save", "Save")}
                </button>
                <button
                  onClick={() => resetRow(cat)}
                  disabled={saving[cat] || !row.is_overridden}
                  className="rounded-full border border-ink-border text-ink-textDim px-2 py-1 text-[10px] font-black hover:text-white disabled:opacity-40"
                  title={t("admin.fareTable.resetRowTip", "Clear this row's override")}
                  data-testid={`fare-reset-${cat}`}
                >
                  {t("common.reset", "Reset")}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-ink-textFaint mt-3 leading-relaxed">
        {t("admin.fareTable.tip", "Tip: leave a field blank when saving to keep the code default for that field only. Changes apply instantly to new orders and fare estimates.")}
      </div>
    </div>
  );
}
